import { DocumentValidationTouchpoint } from './DocumentValidationTouchpoint';
import { UIDocumentControllerTouchPoint } from './UIDocumentControllerTouchPoint';
import { CustomDropdownTouchpoint } from './CustomDropdownTouchpoint';
import { Touchpoint } from '../../ScriptingEngineEnvironment';
import { CloudCaptureDisposition, VFTDisposition } from '../../../../services/ValidationFailureTypes/Api/ViewModels';
import { DocumentValidationConfiguration } from '../../../CJSRuleEngineV2ClientWrapper/ClientValidationController/DocumentValidationConfiguration';
import { transformToDPSValidationResult } from './transformToDPSValidationResults';
import { ExecutionContext, DocumentViewModel } from '../../../../services/backendServices/ViewModels';
import { CustomJSRulesV2 } from '../../..';

import { JSONPath } from 'jsonpath-plus';
import { ValidationFunctionProgrammingError } from '../../../DocumentEditRules/DocumentEditValidator/errors';
import { IDocumentViewModel } from '../../../../services/Document/Api';
import { VFTDictionary } from './createValidationFailureTypeDictionary';
import { PODocumentsForInvoice } from './extractPONumbers';

export class CustomJSEngine {
    constructor(
        private readonly documentValidation: DocumentValidationTouchpoint,
        private readonly uiDocumentController: UIDocumentControllerTouchPoint,
        private readonly customDropdown: CustomDropdownTouchpoint,
        private readonly context: ExecutionContext,
        private readonly vftDictionary: VFTDictionary,
        private readonly documentValidationConfiguration?: DocumentValidationConfiguration
    ) {}

    public async validateDocument(...args: Parameters<DocumentValidationTouchpoint['executeValidationFunctions']>) {
        const validationResults = await this.documentValidation.executeValidationFunctions(...args);

        return validationResults;
    }

    public async controlDocumentUI(...args: Parameters<UIDocumentControllerTouchPoint['executeControllerFunctions']>) {
        await this.uiDocumentController.executeControllerFunctions(...args);
    }

    public async fetchCustomDropdownOptions(
        ...args: Parameters<CustomDropdownTouchpoint['executeCustomDropdownFunctions']>
    ) {
        await this.customDropdown.executeCustomDropdownFunctions(...args);
    }

    /**
     * Mutates the validation results returned by the Buyer Custom JS Rules
     */
    private applyOverridesToValidationResult(
        validationResult: Touchpoint.DocumentValidation.ValidationClassResult,
        validationFailureType?: VFTDictionary[string],
        inputParameterOverride?: DocumentValidationConfiguration['inputParameters'][number]['override']
    ): void {
        const newValidationClass = inputParameterOverride?.ValidationClass ?? null;

        // We don't want to override the validation class when the scripts are running in DPS
        if (newValidationClass !== null && this.context !== ExecutionContext.DPSRule) {
            if (newValidationClass === 'Info') {
                validationResult.validationClass = 'Warning';
                validationResult.result.omitUIPrefix = true;
            } else {
                validationResult.validationClass = newValidationClass;
            }
        }

        const computedDisposition = inputParameterOverride?.Disposition ?? validationFailureType?.Disposition;

        if (computedDisposition === VFTDisposition.Park || computedDisposition === VFTDisposition.ReturnToSupplier) {
            validationResult.result.computedDisposition = computedDisposition;
        }

        const cloudCaptureDisposition = inputParameterOverride?.CloudCaptureDisposition;

        if (
            cloudCaptureDisposition === CloudCaptureDisposition.ReturnToBuyer ||
            cloudCaptureDisposition === CloudCaptureDisposition.SilentRejection
        ) {
            validationResult.result.computedCloudCaptureDisposition = cloudCaptureDisposition;
        }
    }

    private getInputParameterOverrideDictionary() {
        const inputParameters = this.documentValidationConfiguration?.inputParameters ?? [];

        const inputParameterOverrideDictionary = inputParameters.reduce((acc, inputParameter) => {
            if (!inputParameter.ErrorCode) {
                return acc;
            }

            acc[inputParameter.ErrorCode] = inputParameter.override;

            return acc;
        }, {} as Record<string, DocumentValidationConfiguration['inputParameters'][number]['override']>);

        return inputParameterOverrideDictionary;
    }

    public transformToErrorAndWarningLists(
        results: Touchpoint.DocumentValidation.ValidationClassResult[],
        invoice: IDocumentViewModel,
        poDocuments: PODocumentsForInvoice
    ) {
        const inputParameterOverrideDictionary = this.getInputParameterOverrideDictionary();

        results.forEach((result) => {
            const errorCode = result.result.errorCode;

            const inputParameterOverride = inputParameterOverrideDictionary[errorCode];

            const validationFailureType = this.vftDictionary?.[errorCode];

            this.applyOverridesToValidationResult(result, validationFailureType, inputParameterOverride);
        });

        const warnings = results.filter((x) => x.validationClass === 'Warning');
        const errors = results.filter((x) => x.validationClass === 'Error');

        const filteredErrors =
            this.context === ExecutionContext.WebEntry
                ? errors.filter((x) => x.result.computedDisposition === VFTDisposition.ReturnToSupplier)
                : errors;

        return {
            warnings: warnings.map((x) => {
                const override = inputParameterOverrideDictionary[x.result.errorCode] ?? undefined;

                const processedCustomText = override?.TitleText
                    ? this.processCustomTextFromOverride(x, override, invoice, poDocuments)
                    : undefined;

                return transformToDPSValidationResult(x, this.vftDictionary[x.result.errorCode], processedCustomText);
            }),
            errors: filteredErrors.map((x) => {
                const override = inputParameterOverrideDictionary[x.result.errorCode] ?? undefined;

                const processedCustomText = override
                    ? this.processCustomTextFromOverride(x, override, invoice, poDocuments)
                    : undefined;

                return transformToDPSValidationResult(x, this.vftDictionary[x.result.errorCode], processedCustomText);
            }),
        };
    }

    private processCustomTextFromOverride(
        { result }: Touchpoint.DocumentValidation.ValidationClassResult,
        override: CustomJSRulesV2.Utils.ParsedInputParameter,
        invoice: IDocumentViewModel,
        poDocuments: PODocumentsForInvoice
    ):
        | {
              titleText: string;
              titleArguments: string[];
              detailText?: string;
              detailArguments: string[];
          }
        | undefined {
        const { TitleText, TitleArguments, DetailText, DetailArguments } = override;

        if (!TitleText) {
            return undefined;
        }

        const { documentIndexes } = result;

        const extractArgumentFromDocument = (arg: string) => {
            const [extractFrom, jsonPath] = arg.split(':');

            const processedJsonPath = jsonPath.replace(/\[[^\]]*\]/g, (match) => {
                const index = match.replace('[', '').replace(']', '');
                const documentIndex = documentIndexes?.[index];

                if (documentIndex == null) {
                    console.error(
                        `Document index ${index} not found in documentIndexes: ${JSON.stringify(documentIndexes)}`
                    );
                    throw new ValidationFunctionProgrammingError();
                }

                return `[${documentIndex}]`;
            });

            let targetDocument: IDocumentViewModel | DocumentViewModel | null = null;

            switch (extractFrom) {
                case 'inv': {
                    targetDocument = invoice;
                    break;
                }
                case 'po': {
                    // If the result includes an invoice line item index, we need to find the PO document referenced in that invoice line
                    const indexKey: Touchpoint.Config.DocumentIndex = 'invoiceLineItemIdx';

                    const lineItemIndexValue = documentIndexes?.[indexKey];

                    const poDocumentReferencedInInvoiceLine = poDocuments.lineItemPODocuments.find(
                        (line) => line.lineItemIdx === lineItemIndexValue
                    )?.poDocument;

                    if (lineItemIndexValue != null) {
                        targetDocument = poDocumentReferencedInInvoiceLine ?? poDocuments.headerPODocument ?? null;
                    } else {
                        targetDocument = poDocuments.headerPODocument;
                    }

                    break;
                }
                default: {
                    console.error(`Unsupported document type: ${extractFrom}`);
                    throw new ValidationFunctionProgrammingError();
                }
            }

            if (targetDocument == null) {
                return null;
            }

            const [value] = JSONPath({ path: processedJsonPath, json: targetDocument });
            if (value == null) {
                return null;
            }

            return `${value}`;
        };

        const titleArguments = TitleArguments?.split(';') ?? [];

        const titleTextReplacements = titleArguments.map(extractArgumentFromDocument).map((r) => r ?? '');

        const detailArguments = DetailArguments?.split(';') ?? [];

        const detailTextReplacements = detailArguments.map(extractArgumentFromDocument).map((r) => r ?? '');

        return {
            titleText: TitleText,
            titleArguments: titleTextReplacements,
            detailText: DetailText ?? undefined,
            detailArguments: detailTextReplacements,
        };
    }
}
